// @section 1 Per-deal detail — every candidate module on one canvas.
// Each card carries a small "module:<slug>" badge in the top-right so Adam can
// say "keep / drop / move this module" without ambiguity.

import { Fragment, type ReactNode } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import { VERTICAL_LABELS, fmtUsd, fmtDate, fmtDays, type Deal } from '../lib/acquisitions';
import { useAcquisitions } from '../lib/AcquisitionsContext';

export default function AcquisitionDetail() {
  const acquisitions = useAcquisitions();
  const { dealId } = useParams<{ dealId: string }>();
  const d = dealId ? acquisitions.byId(dealId) : undefined;
  if (!d) {
    return (
      <div className="v5-page" style={{ padding: 24 }}>
        <div className="crumb"><NavLink to="/acquisitions" style={{ color: 'var(--brand-3)' }}>← Acquisitions</NavLink></div>
        <div style={{ color: 'var(--neg)', marginTop: 16 }}>Deal not found.</div>
      </div>
    );
  }

  return (
    <div className="v5-page">
      {/* @subsection 1.1 Header */}
      <DetailHeader d={d} />

      <div className="proto-detail-body">
        {/* Column-flow grid: each module card auto-fits */}
        <div className="proto-detail-grid">
          <ModuleStageTimeline d={d} />
          <ModuleNextAction d={d} />
          <ModuleListingOverview d={d} />
          <ModuleTopLineFinancials d={d} />
          <ModuleCimExtracts d={d} />
          <ModuleServiceMix d={d} />
          <ModuleClientMix d={d} />
          <ModuleOpenTasks d={d} />
          <ModuleActivityLog d={d} />
          <ModuleDocuments d={d} />
          <ModuleContacts d={d} />
          <ModuleBroker d={d} />
          <ModuleNotes d={d} />
        </div>
      </div>
    </div>
  );
}

// @section 2 Header
function DetailHeader({ d }: { d: Deal }) {
  const acquisitions = useAcquisitions();
  const code = d.listing.source_listing_id ?? '—';
  const where = [d.listing.location_city, d.listing.location_state].filter(Boolean).join(', ');
  const vertical = d.listing.vertical ? (VERTICAL_LABELS[d.listing.vertical] ?? d.listing.vertical) : '—';
  const stage = acquisitions.stageByKey(d.stage)?.display_name ?? d.stage.replace(/_/g, ' ');
  return (
    <div className="page-head" style={{ paddingBottom: 18 }}>
      <div className="crumb">
        <NavLink to="/acquisitions" style={{ color: 'var(--tx-2)' }}>Acquisitions</NavLink>
        <span className="sep">›</span>
        <b>{code}</b>
      </div>
      <div className="page-title" style={{ marginBottom: 6 }}>
        <h1 style={{ marginRight: 8 }}>{d.listing.business_name}</h1>
        <span className="sub">{vertical} · {where || 'no location'}</span>
        <div className="toolbar" style={{ marginLeft: 'auto' }}>
          <span className="proto-stage-pill">{stage}{d.stage_substatus ? ` · ${d.stage_substatus}` : ''}</span>
          <span className="proto-stage-pill" style={{ background: 'var(--t2-bg)', color: 'var(--tx-2)' }}>
            ask {fmtUsd(d.listing.asking_price)}
          </span>
          <span className="proto-stage-pill" style={{ background: 'var(--t2-bg)', color: 'var(--tx-2)' }}>
            rev {fmtUsd(d.listing.revenue)}
          </span>
          <span className="proto-stage-pill" style={{ background: 'var(--t2-bg)', color: 'var(--tx-2)' }}>
            SDE {fmtUsd(d.listing.cash_flow)}
          </span>
        </div>
      </div>
      <div style={{ fontSize: 11, color: 'var(--tx-4)' }}>
        Last touched {fmtDays(d.summary?.last_touched_at ?? d.updated_at)} · {d.broker?.company_name ?? 'no broker'}
      </div>
    </div>
  );
}

// @section 3 Module card primitive
function Module({
  slug, title, span, children,
}: {
  slug: string;
  title: string;
  span?: 1 | 2;
  children: ReactNode;
}) {
  return (
    <section className={`proto-module${span === 2 ? ' span-2' : ''}`}>
      <header>
        <h3>{title}</h3>
        <span className="mod-tag">module: <code>{slug}</code></span>
      </header>
      <div className="proto-module-body">{children}</div>
    </section>
  );
}

function Empty({ children }: { children: ReactNode }) {
  return <div style={{ fontSize: 12, color: 'var(--tx-4)', fontStyle: 'italic' }}>{children}</div>;
}

// @section 4 Modules

// 4.1 Stage timeline — visual progression through workflow_stages
function ModuleStageTimeline({ d }: { d: Deal }) {
  const acquisitions = useAcquisitions();
  const current = acquisitions.stageByKey(d.stage);
  const stages = acquisitions.stages.filter((s) => !s.is_terminal);
  return (
    <Module slug="stage-timeline" title="Stage timeline" span={2}>
      <div className="path" style={{ display: 'grid', gridTemplateColumns: `repeat(${stages.length}, 1fr)`, gap: 2 }}>
        {stages.map((s) => {
          const done = current ? s.stage_order < current.stage_order : false;
          const isCurr = s.stage_key === d.stage;
          return (
            <div
              key={s.stage_key}
              className={`stage${done ? ' done' : ''}${isCurr ? ' current' : ''}`}
              title={`${s.display_name}${s.default_owner ? ` · owner: ${s.default_owner}` : ''}`}
            >
              {s.display_name}
            </div>
          );
        })}
      </div>
      <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 1, background: 'var(--t2-border)', borderRadius: 6, overflow: 'hidden' }}>
        <DateCell label="Inquiry" value={d.inquiry_date} />
        <DateCell label="NDA Signed" value={d.nda_signed_date} />
        <DateCell label="CIM" value={d.cim_received_date} />
        <DateCell label="Meeting" value={d.meeting_date} />
        <DateCell label="IOI" value={d.ioi_date} />
        <DateCell label="LOI" value={d.loi_date} />
      </div>
    </Module>
  );
}

function DateCell({ label, value }: { label: string; value: string | null }) {
  return (
    <div style={{ background: 'rgba(10,19,34,0.5)', padding: '8px 10px', textAlign: 'center' }}>
      <div style={{ fontSize: 9, color: 'var(--tx-4)', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600 }}>{label}</div>
      <div style={{ fontFamily: 'var(--t-display)', fontSize: 13, fontWeight: 700, color: value ? 'var(--tx-0)' : 'var(--tx-5)' }}>{fmtDate(value)}</div>
    </div>
  );
}

// 4.2 Next action
function ModuleNextAction({ d }: { d: Deal }) {
  return (
    <Module slug="next-action" title="Next action">
      {d.next_action ? (
        <div>
          <div style={{ fontSize: 13, color: 'var(--tx-0)', fontWeight: 500 }}>{d.next_action}</div>
          <div style={{ fontSize: 11, color: 'var(--tx-3)', marginTop: 6 }}>
            Owner: <b style={{ color: 'var(--tx-1)' }}>{d.next_action_owner ?? 'unassigned'}</b>
            {' · '}Priority: <b style={{ color: 'var(--tx-1)' }}>{d.priority}</b>
          </div>
        </div>
      ) : <Empty>No next action set.</Empty>}
    </Module>
  );
}

// 4.3 Listing overview
function ModuleListingOverview({ d }: { d: Deal }) {
  const l = d.listing;
  return (
    <Module slug="listing-overview" title="Listing overview">
      <dl className="proto-dl">
        <dt>Source</dt><dd>{l.source_platform ?? '—'} · <span className="mono">{l.source_listing_id ?? ''}</span></dd>
        <dt>Type</dt><dd>{l.business_type ?? '—'}</dd>
        <dt>Vertical</dt><dd>{l.vertical ? (VERTICAL_LABELS[l.vertical] ?? l.vertical) : '—'}</dd>
        <dt>Location</dt><dd>{[l.location_city, l.location_state, l.region].filter(Boolean).join(', ') || '—'}</dd>
        <dt>Year est.</dt><dd>{l.year_established ?? '—'}</dd>
        <dt>Employees</dt><dd>{l.employees ?? '—'}</dd>
        <dt>Clients</dt><dd>{l.client_count ?? '—'}</dd>
        <dt>Delivery</dt><dd>{l.delivery_model ?? '—'}</dd>
        <dt>Owner terms</dt><dd>{l.owner_terms ?? '—'}</dd>
        <dt>Lease</dt><dd>{l.lease_terms ?? '—'}</dd>
        {l.listing_url && (
          <>
            <dt>Listing</dt>
            <dd><a href={l.listing_url} target="_blank" rel="noreferrer" style={{ color: 'var(--brand-3)' }}>open ↗</a></dd>
          </>
        )}
      </dl>
    </Module>
  );
}

// 4.4 Top-line financials from listing
function ModuleTopLineFinancials({ d }: { d: Deal }) {
  const l = d.listing;
  const askMultRev = l.asking_price && l.revenue ? (l.asking_price / l.revenue).toFixed(2) : null;
  const askMultSde = l.asking_price && l.cash_flow ? (l.asking_price / l.cash_flow).toFixed(2) : null;
  return (
    <Module slug="financials-topline" title="Financials · top-line">
      <div className="proto-stat-grid">
        <Stat label="Ask"      value={fmtUsd(l.asking_price)} />
        <Stat label="Revenue"  value={fmtUsd(l.revenue)} />
        <Stat label="SDE"      value={fmtUsd(l.cash_flow)} />
        <Stat label="EBITDA"   value={fmtUsd(l.ebitda)} />
        <Stat label="× rev"    value={askMultRev ?? '—'} />
        <Stat label="× SDE"    value={askMultSde ?? '—'} />
      </div>
    </Module>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="proto-stat">
      <div className="lbl">{label}</div>
      <div className="val">{value}</div>
    </div>
  );
}

// 4.5 CIM extracts — multi-year financials
function ModuleCimExtracts({ d }: { d: Deal }) {
  const rows = d.cim_extracts;
  return (
    <Module slug="financials-cim" title="Financials · CIM extracts" span={2}>
      {rows.length === 0 ? <Empty>No structured CIM extracts yet.</Empty> : (
        <table className="proto-table">
          <thead>
            <tr>
              <th>Year</th><th>Gross rev</th><th>Cash flow</th><th>EBITDA</th>
              <th>Owner W2</th><th>× rev</th><th>× SDE</th><th>Method</th><th>Conf</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((e) => (
              <tr key={e.extract_id}>
                <td>{e.is_year_summary ? 'TTM' : (e.cim_year ?? '—')}</td>
                <td>{fmtUsd(e.gross_revenue)}</td>
                <td>{fmtUsd(e.cash_flow)}</td>
                <td>{fmtUsd(e.ebitda)}</td>
                <td>{fmtUsd(e.owner_w2)}</td>
                <td className="muted">{e.asking_multiple_rev ?? '—'}</td>
                <td className="muted">{e.asking_multiple_sde ?? '—'}</td>
                <td className="muted">{e.extraction_method}</td>
                <td>
                  <span className={`conf conf-${e.extraction_confidence}`}>{e.extraction_confidence}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Module>
  );
}

// 4.6 Service mix — from cim_extracts.service_mix JSONB
function ModuleServiceMix({ d }: { d: Deal }) {
  const mix = d.cim_extracts.find((e) => e.service_mix)?.service_mix;
  return (
    <Module slug="service-mix" title="Service mix">
      {!mix ? <Empty>No service-mix breakdown in CIM.</Empty> : (
        <PctBars data={mix} />
      )}
    </Module>
  );
}

// 4.7 Client mix + concentration
function ModuleClientMix({ d }: { d: Deal }) {
  const ext = d.cim_extracts.find((e) => e.client_mix || e.client_concentration);
  return (
    <Module slug="client-mix" title="Client mix & concentration">
      {!ext ? <Empty>No client-mix / concentration in CIM.</Empty> : (
        <>
          {ext.client_mix && <PctBars data={ext.client_mix} />}
          {ext.client_concentration && (
            <dl className="proto-dl" style={{ marginTop: 10 }}>
              {Object.entries(ext.client_concentration).map(([k, v]) => (
                <Fragment key={k}>
                  <dt>{k.replace(/_/g, ' ')}</dt>
                  <dd>{typeof v === 'number' ? v : String(v)}</dd>
                </Fragment>
              ))}
            </dl>
          )}
        </>
      )}
    </Module>
  );
}

function PctBars({ data }: { data: Record<string, number> }) {
  const entries = Object.entries(data).filter(([, v]) => typeof v === 'number');
  if (entries.length === 0) return <Empty>No values.</Empty>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {entries.map(([k, v]) => (
        <div key={k}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
            <span style={{ color: 'var(--tx-2)' }}>{k.replace(/_pct$/, '').replace(/_/g, ' ')}</span>
            <span style={{ color: 'var(--tx-1)', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{v}%</span>
          </div>
          <div style={{ height: 4, background: 'rgba(255,255,255,0.06)', borderRadius: 4, marginTop: 3, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${Math.min(100, v)}%`, background: 'var(--brand-grad)', borderRadius: 4 }} />
          </div>
        </div>
      ))}
    </div>
  );
}

// 4.8 Open tasks
function ModuleOpenTasks({ d }: { d: Deal }) {
  const tasks = d.open_tasks;
  return (
    <Module slug="open-tasks" title={`Open tasks · ${tasks.length}`}>
      {tasks.length === 0 ? <Empty>No open tasks.</Empty> : (
        <ul className="proto-list">
          {tasks.map((t) => (
            <li key={t.task_id} className="proto-list-row" style={{ alignItems: 'flex-start' }}>
              <div style={{ flex: 1, marginRight: 12 }}>
                <div style={{ color: 'var(--tx-1)' }}>{t.summary}</div>
                <div style={{ fontSize: 10, color: 'var(--tx-4)', marginTop: 2 }}>
                  {t.task_kind} · owner {t.owner} · due {fmtDate(t.due_at)}
                </div>
              </div>
              <span className={`proto-stage-pill ${t.urgency === 'overdue' ? 'urgency-overdue' : ''}`}>
                {t.urgency ?? t.priority ?? ''}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Module>
  );
}

// 4.9 Activity log
function ModuleActivityLog({ d }: { d: Deal }) {
  const acts = d.activities.slice(0, 12);
  return (
    <Module slug="activity-log" title={`Activity · ${d.activities.length}`} span={2}>
      {acts.length === 0 ? <Empty>No activity logged yet.</Empty> : (
        <ul className="proto-list">
          {acts.map((a) => (
            <li key={a.activity_id} className="proto-list-row" style={{ alignItems: 'flex-start' }}>
              <div style={{ width: 110, color: 'var(--tx-4)', fontSize: 10, marginRight: 12, flexShrink: 0 }}>
                <div>{fmtDate(a.completed_at)}</div>
                <div style={{ marginTop: 1 }}>{a.activity_type}</div>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: 'var(--tx-1)' }}>
                  <b style={{ marginRight: 6 }}>{a.counterparty_name ?? a.owner}</b>
                  {a.outcome && <span style={{ fontSize: 10, color: 'var(--tx-3)' }}>· {a.outcome}</span>}
                </div>
                {a.summary && (
                  <div style={{ fontSize: 11, color: 'var(--tx-2)', marginTop: 2, lineHeight: 1.45, whiteSpace: 'pre-wrap' }}>
                    {a.summary.length > 280 ? a.summary.slice(0, 280) + '…' : a.summary}
                  </div>
                )}
                {a.next_action && (
                  <div style={{ fontSize: 10, color: 'var(--brand-4)', marginTop: 4 }}>
                    → {a.next_action}{a.next_action_due ? ` (due ${fmtDate(a.next_action_due)})` : ''}
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Module>
  );
}

// 4.10 Documents
function ModuleDocuments({ d }: { d: Deal }) {
  return (
    <Module slug="documents" title={`Documents · ${d.documents.length}`}>
      {d.documents.length === 0 ? <Empty>No CIM, NDA, or other documents on file.</Empty> : (
        <ul className="proto-list">
          {d.documents.map((doc) => (
            <li key={doc.doc_id} className="proto-list-row" style={{ alignItems: 'flex-start' }}>
              <div style={{ flex: 1, marginRight: 8 }}>
                <div style={{ color: 'var(--tx-1)' }}>{doc.document_name ?? doc.storage_url ?? '(unnamed)'}</div>
                <div style={{ fontSize: 10, color: 'var(--tx-4)', marginTop: 2 }}>
                  {fmtDate(doc.received_date)} · <span className="mono">{doc.storage_url}</span>
                </div>
              </div>
              <span className="proto-stage-pill">{doc.document_type}</span>
            </li>
          ))}
        </ul>
      )}
    </Module>
  );
}

// 4.11 Contacts
function ModuleContacts({ d }: { d: Deal }) {
  return (
    <Module slug="contacts" title={`Contacts · ${d.contacts.length}`}>
      {d.contacts.length === 0 ? <Empty>No deal-level contacts yet.</Empty> : (
        <ul className="proto-list">
          {d.contacts.map((c, i) => (
            <li key={i} className="proto-list-row" style={{ alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <div style={{ color: 'var(--tx-1)' }}>{c.contact_name ?? '—'}</div>
                <div style={{ fontSize: 10, color: 'var(--tx-4)', marginTop: 2 }}>
                  {[c.contact_email, c.contact_phone].filter(Boolean).join(' · ') || '—'}
                </div>
              </div>
              <span className="proto-stage-pill">{c.role ?? 'role'}</span>
            </li>
          ))}
        </ul>
      )}
    </Module>
  );
}

// 4.12 Broker
function ModuleBroker({ d }: { d: Deal }) {
  const b = d.broker;
  return (
    <Module slug="broker" title="Broker">
      {!b ? <Empty>No broker linked.</Empty> : (
        <dl className="proto-dl">
          <dt>Company</dt><dd>{b.company_name}</dd>
          <dt>Rep</dt><dd>{b.broker_name ?? '—'}</dd>
          <dt>Email</dt><dd className="mono">{b.email ?? '—'}</dd>
          <dt>Phone</dt><dd className="mono">{b.phone ?? '—'}</dd>
          <dt>Site</dt><dd>{b.website ? <a href={b.website} target="_blank" rel="noreferrer" style={{ color: 'var(--brand-3)' }}>{b.website}</a> : '—'}</dd>
          <dt>Platform</dt><dd>{b.platform ?? '—'}</dd>
          <dt>Relation</dt><dd>{b.relationship_status ?? '—'}</dd>
        </dl>
      )}
    </Module>
  );
}

// 4.13 Notes / description / reason for sale
function ModuleNotes({ d }: { d: Deal }) {
  const l = d.listing;
  return (
    <Module slug="notes" title="Notes & narrative" span={2}>
      {d.notes && (
        <Block label="Deal notes" body={d.notes} />
      )}
      {l.reason_for_sale && (
        <Block label="Reason for sale" body={l.reason_for_sale} />
      )}
      {l.description && (
        <Block label="Listing description" body={l.description} />
      )}
      {!d.notes && !l.reason_for_sale && !l.description && (
        <Empty>No narrative captured yet.</Empty>
      )}
    </Module>
  );
}

function Block({ label, body }: { label: string; body: string }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 9, color: 'var(--tx-4)', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 12, color: 'var(--tx-1)', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{body}</div>
    </div>
  );
}
